/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/*
! These eslint rules are disabled because there's no reason for us to have to
! type out every single prop that we want to pass down to the Typeahead component.
*/
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-array-index-key */

import React, { ForwardRefRenderFunction, useRef, useState } from "react";
import { Hint, Menu, MenuItem, Typeahead, TypeaheadRef } from "react-bootstrap-typeahead";
import { Form, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import type { Option } from "react-bootstrap-typeahead/types/types";
import type TypeaheadType from "react-bootstrap-typeahead/types/core/Typeahead";

import type { MenuRenderParameters, PowerShadesTypeaheadProps } from "./types";

import "./PowerShadesTypeahead.scss";
import { hasCustomOptionAndLabelProperty, isOptionObject } from "../psUtil";

const getOptionLabel = (option: Option): string => {
	if (isOptionObject(option) && !option.customOption) {
		return String(option.label ?? "");
	}
	if (hasCustomOptionAndLabelProperty(option)) {
		return option.label;
	}
	return String(option);
};

const isSameOption = (left: Option, right: Option): boolean => (
	getOptionLabel(left) === getOptionLabel(right)
);

const getSelectedSummary = (selected: Option[], maxVisible = 2): string => {
	const labels = selected.map(getOptionLabel).filter(Boolean);
	if (labels.length === 0) return "";
	if (labels.length <= maxVisible) return labels.join(", ");
	return `${labels.slice(0, maxVisible).join(", ")} +${labels.length - maxVisible} more`;
};

const buildCheckboxMenuOptions = (
	allOptions: Option[],
	selected: Option[],
	filteredResults: Option[],
): Option[] => {
	const selectedInMenu = allOptions.filter((option) => (
		selected.some((item) => isSameOption(item, option))
	));

	selected.forEach((item) => {
		if (!selectedInMenu.some((option) => isSameOption(option, item))) {
			selectedInMenu.push(item);
		}
	});

	const unselectedPool = filteredResults.length > 0 ? filteredResults : allOptions;
	const unselectedInMenu = unselectedPool.filter((option) => (
		!selected.some((item) => isSameOption(item, option))
	));

	return [...selectedInMenu, ...unselectedInMenu];
};

const PowerShadesTypeahead: ForwardRefRenderFunction<TypeaheadType, PowerShadesTypeaheadProps> = (props) => {
	const [open, setOpen] = useState(false);
	const shouldBeDisabled = (props.isLoading || props.disabled) ?? false;
	const ref = useRef<TypeaheadRef>(null);

	const { clearButton = false } = ref.current?.props as any || {};

	const isCheckboxMenu = Boolean(props.multiple && props.checkboxMenu);

	let realSelected = props?.selected ?? [];
	if (realSelected) {
		if (realSelected.length === 1 && !realSelected[0]) {
			realSelected = [];
		}
	}

	const inputRender = ({ inputRef, referenceElementRef, ...inputProps }) => {
		const hasSearchText = Boolean(inputProps?.value?.length);
		const showSelectedSummary = isCheckboxMenu && !open && realSelected.length > 0 && !hasSearchText;
		const selectedSummary = getSelectedSummary(realSelected);
		const displayValue = showSelectedSummary ? selectedSummary : inputProps.value;
		const hasValue = Boolean(displayValue?.length);
		const inputPlaceholder = isCheckboxMenu
			? (open && realSelected.length > 0 ? "Search..." : (props.placeholder ?? "Select..."))
			: inputProps.placeholder;
		const newClassName = `
		start-0 rounded
		${inputProps.className}
		${props.errored && "border-danger"}
		${props.inputClass ?? ""}
		${clearButton && "pe-5"}
		${showSelectedSummary ? "typeahead-checkbox-summary-input" : ""}
		`;
		const showDropdownIcon = isCheckboxMenu || !hasValue;

		return (
			<Hint >
				<InputGroup className={`position-relative ${clearButton && "typeahead-editing"}`}>
					<Form.Control
						{...inputProps}
						ref={(node) => {
							inputRef(node);
							referenceElementRef(node);
						}}
						value={displayValue ?? ""}
						placeholder={inputPlaceholder}
						readOnly={showSelectedSummary}
						title={showSelectedSummary ? realSelected.map(getOptionLabel).join(", ") : undefined}
						className={newClassName}
					/>
					{showDropdownIcon && (
						<InputGroup.Text
							className={`${props.errored && "text-danger"}
								position-absolute end-0 h-100 custom-group-text
								${shouldBeDisabled ? "disabled" : ""}
								${clearButton && realSelected.length > 0 ? "typeahead-checkbox-chevron-with-clear" : ""}`}
							onClick={(e) => {
								e.preventDefault();
								if (shouldBeDisabled) return;
								setOpen(true);
								const element = e.currentTarget.previousElementSibling as HTMLInputElement;
								element?.focus();
							}}
						>
							<FontAwesomeIcon icon={faAngleDown} />
						</InputGroup.Text>
					)}
				</InputGroup>
			</Hint>
		);
	};

	const menuRender = ({ results, menuProps, state }: MenuRenderParameters) => {
		const widthOfInputBar = parseInt(menuProps.style?.width?.toString()?.slice(0, -2) ?? "", 10);
		const customInputText = document.querySelector(".custom-group-text");
		const customInputTextWidth = customInputText?.clientWidth ?? 0;
		const updatedWidth = widthOfInputBar + customInputTextWidth;
		const updatedWidthString = `${updatedWidth}px`;
		const updatedMenuStyle = { ...menuProps.style, width: updatedWidthString };
		const updatedMenuProps = { ...menuProps, style: updatedMenuStyle, className: `${menuProps.className} z-3` };
		const addSelectionText = menuProps.newSelectionPrefix ?? "Add Item:";

		const inputValue = ref.current?.getInput()?.value?.trim().toLowerCase();
		const isInputMatchingSelected
			= realSelected.length === 1 && inputValue === realSelected[0]?.toString().toLowerCase();
		const baseResults = isInputMatchingSelected ? props.options as Option[] : results;
		const displayResult = props.multiple && props.checkboxMenu
			? buildCheckboxMenuOptions(props.options as Option[], realSelected, results)
			: baseResults;

		const toggleCheckboxOption = (option: Option) => {
			const isSelected = realSelected.some((item) => isSameOption(item, option));
			const nextSelected = isSelected
				? realSelected.filter((item) => !isSameOption(item, option))
				: [...realSelected, option];
			props.onChange?.(nextSelected);
		};

		if (props.multiple && props.checkboxMenu) {
			return (
				<Menu {...updatedMenuProps} className="typeahead-menu typeahead-checkbox-menu">
					{displayResult.map((result) => {
						const label = getOptionLabel(result);
						const checked = realSelected.some((item) => isSameOption(item, result));

						return (
							<div
								key={label}
								className="dropdown-item power-shades-typeahead-checkbox-item"
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => toggleCheckboxOption(result)}
							>
								<Form.Check
									type="checkbox"
									id={`${props.id ?? "typeahead"}-${label}`}
									checked={checked}
									label={label}
									readOnly
									className="mb-0 user-select-none"
								/>
							</div>
						);
					})}
				</Menu>
			);
		}

		return (
			<Menu {...updatedMenuProps} className="typeahead-menu">
				{displayResult.map((result, index) => (
					<MenuItem key={index} option={result} position={index}>
						{isOptionObject(result) && !result.customOption
							? result.label
							: hasCustomOptionAndLabelProperty(result)
								? `${addSelectionText} ${result.label}`
								: result}
					</MenuItem>
				))}
			</Menu>
		);
	};

	const handleFocus = (event: React.SyntheticEvent<HTMLInputElement>) => {
		const focusEvent = event as React.FocusEvent<HTMLInputElement>;
		const relatedTarget = focusEvent.relatedTarget as HTMLElement | null;
	
		if (!relatedTarget?.className.includes("btn")) {
			setOpen(true);
		}
	};

	const handleContainerClick = () => {
		if (shouldBeDisabled) return;
		
		const inputElement = ref.current?.getInput();
		if (inputElement) {
			inputElement.focus();
		}
		
		setOpen(true);
	};

	return (
		<div onClick={handleContainerClick} className="flex-grow-1">
			<Typeahead
				ref={ref}
				{...props}
				clearButton={props.clearButton ?? true}
				className={`custom-typeahead ${isCheckboxMenu ? "typeahead-checkbox-input" : ""} ${clearButton && "typeahead-editing"}`}
				renderInput={inputRender}
				renderMenu={(results, menuProps, state) => menuRender({ results, menuProps, state })}
				{...(isCheckboxMenu || props.renderToken ? {
					renderToken: isCheckboxMenu ? () => <></> : props.renderToken!,
				} : {})}
				selected={realSelected}
				open={open}
				onInputChange={() => setOpen(true)}
				disabled={shouldBeDisabled}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === "Escape" || e.key === "Tab") {
						setOpen(false);
					}
				}}
				onChange={(selected) => {
					if (props.multiple) {
						props.onChange?.(selected);
						return;
					}
					handleContainerClick();
					if (selected.length === 0) {
						props.onChange?.([]);
						return;
					}
					setOpen(false);
					props.onChange?.(selected);
				}}
				onFocus={handleFocus}
				onBlur={() => setOpen(false)}
			/>
		</div>
	);
};

export default React.forwardRef(PowerShadesTypeahead);
